import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";

/**
 * Initialize Capacitor native features.
 * Call this once when the React app mounts.
 * No-op on web — all calls are guarded by isNativePlatform().
 */
export async function initCapacitor() {
  if (!Capacitor.isNativePlatform()) return;

  // Status bar: dark background, light text
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#040C18" });
    }
  } catch (e) {
    console.error("[Capacitor] StatusBar error:", e);
  }

  // Hide splash screen after React has mounted
  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    console.error("[Capacitor] SplashScreen error:", e);
  }
}

/**
 * Set up Android hardware back button handler.
 * @param {Function} canGoBack - returns true if router has history
 * @param {Function} goBack - navigates back
 */
export function setupBackButton(canGoBack, goBack) {
  if (Capacitor.getPlatform() !== "android") return null;

  const listener = CapApp.addListener("backButton", ({ canGoBack: webCanGoBack }) => {
    if (webCanGoBack || canGoBack()) {
      goBack();
    } else {
      CapApp.exitApp();
    }
  });

  return listener;
}

/**
 * Listen for deep link / URL scheme opens.
 * @param {Function} onUrl - callback with URL string
 * @returns cleanup function
 */
export function onDeepLink(onUrl) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = CapApp.addListener("appUrlOpen", (event) => {
    onUrl(event.url);
  });

  return () => listener.then((h) => h.remove());
}

/**
 * Check if running inside a native Capacitor shell.
 */
export function isNative() {
  return Capacitor.isNativePlatform();
}
